import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TradingToast, useToast } from '@trading-ui/core';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAppStore from '@/store/app.store';

import {
  createCompanyUser,
  createPlant,
  deleteCompanyUser,
  deletePlant,
  getCompanyDetails,
  updateCompanyUser,
  updatePlant,
} from '@/api/services/settings/company-details.service';

import { QUERY_KEYS } from '@/api/config/query-keys';
import { ROLES } from '@/constants/roles';

import { parseBackendReason } from '@/utils/error.utils';

import type { CreateCompanyUserPayload, CreatePlantPayload, Plant, ProcessedBackendError, User } from '@/interfaces';

export const useCompanyDetails = (companyId: number) => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const user = useAppStore((state) => state.user);

  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [companyPlants, setCompanyPlants] = useState<Plant[]>([]);
  const [deletingCupsId, setDeletingCupsId] = useState<number | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  const { data, isLoading, error, isRefetching } = useQuery({
    queryKey: [QUERY_KEYS.COMPANY_DETAILS, companyId],
    queryFn: () => getCompanyDetails(companyId),
    enabled: companyId > 0,
  });

  // Reset state when companyId changes
  useEffect(() => {
    setCompanyUsers([]);
    setCompanyPlants([]);
  }, [companyId]);

  useEffect(() => {
    if (data && !isLoading) {
      // Extract users from userCompanies relation
      const users = (data.userCompanies ?? []).map((uc) => uc.user).filter((u): u is User => u !== undefined);
      setCompanyUsers(users);
      setCompanyPlants(data.plants ?? []);
    }
  }, [data, isLoading]);

  useEffect(() => {
    if (error) {
      const errorMessage = parseBackendReason(error as Error | ProcessedBackendError);
      addToast(<TradingToast type="error" title="Error" message={errorMessage} />);
    }
  }, [error, addToast]);

  const handleCreatePlant = useCallback(
    async (payload: CreatePlantPayload) => {
      try {
        await createPlant(payload);
        if (user?.role === ROLES.MOEVE_ADMIN) queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PENDING_ACTIONS] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_DETAILS, companyId] });
        addToast(<TradingToast message={t('services.companyDetails.createCupsSuccess')} type="success" />);
      } catch (e) {
        const errorMessage = parseBackendReason(e as Error | ProcessedBackendError);
        addToast(<TradingToast message={errorMessage} title="Error" type="error" />);
      }
    },
    [user?.role, queryClient, companyId, addToast, t]
  );

  const handleUpdatePlant = useCallback(
    async (id: number, payload: Partial<CreatePlantPayload>) => {
      try {
        await updatePlant(id, payload);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_DETAILS, companyId] });
        // Invalidate masters to refresh plants pricing index data
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BIOFLUX_MASTERS] });
        addToast(<TradingToast message={t('services.companyDetails.updateCupsSuccess')} type="success" />);
      } catch (e) {
        const errorMessage = parseBackendReason(e as Error | ProcessedBackendError);
        addToast(<TradingToast message={errorMessage} title="Error" type="error" />);
      }
    },
    [queryClient, companyId, addToast, t]
  );

  const handleDeletePlant = useCallback(
    async (id: number) => {
      try {
        setDeletingCupsId(id);
        await deletePlant(id);
        if (user?.role === ROLES.MOEVE_ADMIN)
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_COMPANIES] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_DETAILS, companyId] });
        addToast(<TradingToast message={t('services.companyDetails.deleteCupsSuccess')} type="success" />);
      } catch (e) {
        const errorMessage = parseBackendReason(e as Error | ProcessedBackendError);
        addToast(<TradingToast message={errorMessage} type="error" />);
      } finally {
        setDeletingCupsId(null);
      }
    },
    [queryClient, companyId, addToast, user?.role, t]
  );

  const handleCreateUser = useCallback(
    async (payload: CreateCompanyUserPayload) => {
      try {
        await createCompanyUser(payload);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_DETAILS, companyId] });
        addToast(<TradingToast message={t('services.companyDetails.createUserSuccess')} type="success" />);
      } catch (e) {
        const errorMessage = parseBackendReason(e as Error | ProcessedBackendError);
        addToast(<TradingToast message={errorMessage} type="error" />);
      }
    },
    [queryClient, companyId, addToast, t]
  );

  const handleUpdateUser = useCallback(
    async (id: number, payload: Partial<CreateCompanyUserPayload>) => {
      try {
        await updateCompanyUser(id, { ...payload, companyId });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_DETAILS, companyId] });
        addToast(<TradingToast message={t('services.companyDetails.updateUserSuccess')} type="success" />);
      } catch (e) {
        const errorMessage = parseBackendReason(e as Error | ProcessedBackendError);
        addToast(<TradingToast message={errorMessage} type="error" />);
      }
    },
    [queryClient, companyId, addToast, t]
  );

  const handleDeleteUser = useCallback(
    async (userToDelete: User) => {
      if (userToDelete.email === user?.email) {
        addToast(<TradingToast message={t('services.companyDetails.deleteCurrentUserError')} type="error" />);
        return;
      }

      try {
        setDeletingUserId(userToDelete.id);
        await deleteCompanyUser(userToDelete.id, companyId);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_DETAILS, companyId] });
        addToast(<TradingToast message={t('services.companyDetails.deleteUserSuccess')} type="success" />);
      } catch (e) {
        const errorMessage = parseBackendReason(e as Error | ProcessedBackendError);
        addToast(<TradingToast message={errorMessage} type="error" />);
      } finally {
        setDeletingUserId(null);
      }
    },
    [queryClient, companyId, addToast, user?.email, t]
  );

  return {
    companyUsers,
    companyPlants,
    isLoading,
    error,
    isRefetching,
    deletingCupsId,
    deletingUserId,
    handleCreatePlant,
    handleUpdatePlant,
    handleDeletePlant,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
  };
};

export default useCompanyDetails;
